import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import FactCheckIcon from '@mui/icons-material/FactCheck'
import API from '../../../services/API'
import { GlobalContext, UserContext } from '../../../App'
import ReadinessRings from '../../cqcCentre/components/ReadinessRings'
import { KEY_QUESTION_COLORS } from '../../cqcCentre/severity'

const KEY_QUESTIONS = ['Safe', 'Effective', 'Caring', 'Responsive', 'Well-led']

// Per-key-question score bars shown beside the rings in the wide (embedded)
// layout - same identity colours as the rings themselves.
const KeyQuestionBreakdown = ({ assessedScores, evidenceCounts }) => (
  <Stack spacing={1.25} flex={1} minWidth={0} justifyContent="center">
    {KEY_QUESTIONS.map((kq) => {
      const score = assessedScores?.[kq]
      const assessed = score !== null && score !== undefined
      const count = evidenceCounts?.[kq] ?? 0
      return (
        <Tooltip
          key={kq}
          title={
            assessed
              ? `${kq}: ${score} / 100 — ${count} evidence item${count === 1 ? '' : 's'}`
              : `${kq}: no evidence recorded yet`
          }
        >
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
              <Stack direction="row" alignItems="center" spacing={0.75}>
                <Box
                  aria-hidden="true"
                  width={9}
                  height={9}
                  borderRadius="3px"
                  bgcolor={KEY_QUESTION_COLORS[kq]}
                />
                <Typography variant="caption" fontWeight={600}>
                  {kq}
                </Typography>
              </Stack>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                  color: assessed ? KEY_QUESTION_COLORS[kq] : 'text.secondary',
                }}
              >
                {assessed ? score : '—'}
              </Typography>
            </Stack>
            <Box
              sx={{
                mt: 0.5,
                height: 6,
                borderRadius: 3,
                bgcolor: 'anMuted.main',
                overflow: 'hidden',
              }}
            >
              {assessed && (
                <Box
                  sx={{
                    width: `${score}%`,
                    height: '100%',
                    borderRadius: 3,
                    bgcolor: KEY_QUESTION_COLORS[kq],
                  }}
                />
              )}
            </Box>
          </Box>
        </Tooltip>
      )
    })}
  </Stack>
)

// Home-page CQC readiness rings for site admins and Governance team members.
// Renders nothing for everyone else, on tiers below the CQC Centre (tier 2,
// unless in trial), or if the scores feed fails.
//
// `embedded` renders the card as a plain full-width Paper (for stacking under
// Team Actions in the dashboard's right column) with a larger, two-part
// layout; the default renders a self-contained small grid tile.
const CqcReadinessCard = ({ embedded = false }) => {
  const { user } = useContext(UserContext)
  const { tenantInfo } = useContext(GlobalContext)
  const navigate = useNavigate()
  const [scores, setScores] = useState(null)

  const isGovernance =
    user?.security?.isSiteAdmin ||
    user?.employee?.teams?.includes('Governance')
  const inTrial =
    tenantInfo?.inInitialTrialPeriod &&
    tenantInfo?.trialEnd &&
    moment(tenantInfo.trialEnd).isAfter(moment())
  const hasTier = Number(tenantInfo?.subscriptionTier ?? 0) >= 2 || inTrial

  useEffect(() => {
    if (!isGovernance || !hasTier) return
    API.get('/cqc-centre/scores')
      .then((res) => {
        if (res.status === 200) setScores(res.data)
      })
      .catch((e) => console.log(e))
  }, [isGovernance, hasTier])

  if (!isGovernance || !hasTier || !scores) return null

  // A key question with zero evidence hasn't been assessed - null its score
  // so the ring stays an empty track rather than a vacuous 100.
  const counts = scores.evidenceCountsByKeyQuestion
  const assessedScores = Object.fromEntries(
    Object.entries(scores.keyQuestionScores ?? {}).map(([kq, score]) => [
      kq,
      counts && (counts[kq] ?? 0) === 0 ? null : score,
    ]),
  )
  const anyAssessed = Object.values(assessedScores).some(
    (score) => score !== null && score !== undefined,
  )

  const caption = anyAssessed
    ? scores.lastRecomputedAt
      ? `Self-assessed readiness · updated ${moment(scores.lastRecomputedAt).fromNow()}`
      : 'Self-assessed readiness'
    : 'No evidence assessed yet — open the CQC Centre to get started.'

  const header = (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Stack direction="row" gap={1} alignItems="center">
        <FactCheckIcon fontSize="small" sx={{ color: '#1F3D7C' }} />
        <Typography variant="title3">CQC Readiness</Typography>
      </Stack>
      <Button size="small" onClick={() => navigate('/cqc-centre')}>
        Open CQC Centre
      </Button>
    </Stack>
  )

  if (embedded) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5 }}>
        <Stack spacing={1.5}>
          {header}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1.5, sm: 4 }}
            alignItems="center"
          >
            <ReadinessRings
              keyQuestionScores={assessedScores}
              overallScore={anyAssessed ? scores.overallScore : null}
              size={260}
              showLegend={false}
            />
            <KeyQuestionBreakdown
              assessedScores={assessedScores}
              evidenceCounts={counts}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary" textAlign="center">
            {caption}
          </Typography>
        </Stack>
      </Paper>
    )
  }

  return (
    <Grid item xs={12} md={6} lg={4}>
      <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
        <Stack spacing={1} height="100%">
          {header}
          <ReadinessRings
            keyQuestionScores={assessedScores}
            overallScore={anyAssessed ? scores.overallScore : null}
            size={210}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            {caption}
          </Typography>
        </Stack>
      </Paper>
    </Grid>
  )
}

export default CqcReadinessCard
